import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { pluralize } from '@/lib/string-utils.js';
import { type CoachPoolWithMatchCount } from '@airreading/common/data';

import { CoachPoolFilterChips } from './coach-pool-filter-chips.js';
import { teacherPoolUpdatedLabel } from './teacher-pool-summary.js';
import { useCoachPoolMutation } from './use-coach-pool-mutation.js';

interface CoachPoolListProps {
  pools: CoachPoolWithMatchCount[];
  isLoading: boolean;
  isError: boolean;
  onViewPool: (pool: CoachPoolWithMatchCount) => void;
}

// List of coach pools in the side drawer
export const CoachPoolList = ({ pools, isLoading, isError, onViewPool }: CoachPoolListProps) => {
  const { requestToggle, isUpdating, requestRemove, isRemoving, dialogs } = useCoachPoolMutation();
  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Loading teacher pools
      </p>
    );
  }

  if (isError) {
    return <p className="text-sm text-muted-foreground">Teacher pools could not be loaded.</p>;
  }

  if (pools.length === 0) {
    return <p className="text-sm text-muted-foreground">No teacher pools yet.</p>;
  }

  return (
    <>
      <ul className="mt-4 flex flex-col gap-3" data-testid="teachers-availabilities-pool-list">
        {pools.map((pool) => {
          const headingId = `teacher-pool-${pool.id}-heading`;
          const teacherLabel = pluralize(pool.matchingTeachersCount, 'teacher', 'teachers');
          const updating = isUpdating(pool.id);
          const removing = isRemoving(pool.id);
          const activeAction = pool.isActive ? 'Deactivate' : 'Activate';

          return (
            <li key={pool.id}>
              <article
                aria-labelledby={headingId}
                className="rounded-md border bg-white p-4"
                data-testid={`teachers-availabilities-pool-card-${pool.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 id={headingId} className="font-semibold">
                        {pool.title}
                      </h3>
                      <Badge
                        className={
                          pool.isActive
                            ? 'border-transparent bg-green-100 text-green-700 hover:bg-green-100'
                            : 'border-transparent bg-blue-100 text-blue-700 hover:bg-blue-100'
                        }
                      >
                        {pool.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <span className="text-sm font-semibold">
                        {pool.matchingTeachersCount} matching {teacherLabel}
                      </span>
                    </div>
                    {pool.description?.trim() ? (
                      <p className="mt-1 text-sm text-muted-foreground">{pool.description.trim()}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
                      aria-label={`Remove ${pool.title}`}
                      aria-busy={removing}
                      disabled={removing}
                      data-testid={`teachers-availabilities-pool-button-remove-${pool.id}`}
                      onClick={() => requestRemove(pool)}
                    >
                      Remove pool
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="dismiss"
                      aria-label={`${activeAction} ${pool.title}`}
                      aria-busy={updating}
                      disabled={updating}
                      data-testid={`teachers-availabilities-pool-button-toggle-${pool.id}`}
                      onClick={() => requestToggle(pool)}
                    >
                      {activeAction}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      data-testid={`teachers-availabilities-pool-button-view-${pool.id}`}
                      onClick={() => onViewPool(pool)}
                    >
                      View pool
                    </Button>
                  </div>
                </div>
                <CoachPoolFilterChips filters={pool} className="mt-3" />
                <p className="mt-3 text-sm text-muted-foreground">{teacherPoolUpdatedLabel(pool.updatedAt)}</p>
              </article>
            </li>
          );
        })}
      </ul>
      {dialogs}
    </>
  );
};
